import { observer } from 'mobx-react-lite'
import * as React from 'react'
import {
  FlexStyle,
  Image,
  ImageSourcePropType,
  ImageStyle,
  Platform,
  Text,
  TextStyle,
  TouchableOpacity,
  View,
  ViewStyle
} from 'react-native'
import { useThrottledCallback } from 'use-debounce'

import pokerlogo from '../../../assets/mainHeader/poker-power-logo.png'
import backIcon from '../../../assets/mainHeader/back.png'
import PokerLogo from '../../../assets/mainHeader/poker-power-logo.svg'
import CrownIcon from '../../assets/crown-without-fill.svg'
import { DS } from '../../design-system'
import { useStore } from '../../models'
import { CheatSheet } from '../CheatSheet/CheatSheet'
import { useCheatSheet } from '../CheatSheet/useCheatSheet'
import { ViewObserverButton } from '../Observer/observer'
import { styles } from './styles'
import { isWeb } from '../WebWrapper'

import { DiamondCountDisplay } from '@/components/Diamonds/Diamonds'

export interface MainHeaderProps {
  leftIcon?: ImageSourcePropType
  rightIcon?: ImageSourcePropType
  leftIconStyle?: ImageStyle
  rightIconStyle?: ImageStyle
  title?: string
  titleStyle?: TextStyle
  navigate?: () => void
  leftPress?: () => void
  rightPress?: () => void
  from?: string
  hideLeftDefaultIcon?: boolean
  headerContainerStyle?: ViewStyle
  observerPlayers?: string[]
  showEyeIcon?: boolean
  hideLogo?: boolean
  showPremiumIcon?: boolean
  showDarkKeyIcon?: boolean
  hideBackButton?: boolean
  showDiamonds?: boolean
  showDarkDiamondsText?: boolean
}

export const MainHeader = observer((props: MainHeaderProps) => {
  const {
    leftIcon,
    rightIcon,
    from,
    title,
    leftPress,
    rightPress,
    leftIconStyle,
    hideLeftDefaultIcon = false,
    rightIconStyle,
    titleStyle,
    headerContainerStyle,
    observerPlayers,
    showEyeIcon,
    hideLogo,
    showPremiumIcon,
    showDarkKeyIcon = true,
    hideBackButton = false,
    showDiamonds = true,
    showDarkDiamondsText = true
  } = props
  const {
    memberships: { isSubscribed },
    session: { isAdmin, isSuperAdmin }
  } = useStore()
  const isLeftItemVisible = from !== 'Admin' && !hideLeftDefaultIcon
  const isLeftItemDisplay: FlexStyle['display'] = isLeftItemVisible ? 'flex' : 'none'
  const pokerLogo =
    Platform.OS === 'web' ? (
      <Image
        style={[styles.logoIcon, { display: hideLogo ? 'none' : 'flex' }]}
        source={pokerlogo}
        resizeMode="contain"
      />
    ) : (
      <PokerLogo style={[styles.logoIcon, { display: hideLogo ? 'none' : 'flex' }]} />
    )
  const {
    monday: { numberOfDaysPassedPostEvent }
  } = useStore()

  const { open: openCheatSheet } = useCheatSheet()
  const onOpenCheatSheet = useThrottledCallback(() => {
    openCheatSheet()
  }, 350)

  return (
    <View style={[styles.headerContainer, { ...headerContainerStyle }]}>
      {isWeb ? (
        <>
          <View style={[styles.itemLeftContainer]}>
            <TouchableOpacity
              style={{ ...styles.itemLeft, display: isLeftItemDisplay, opacity: hideBackButton ? 0 : 1 }}
              onPress={leftPress}
              disabled={hideBackButton}
            >
              <Image
                style={[styles.leftIcon, { ...leftIconStyle }]}
                source={leftIcon ? leftIcon : backIcon}
                resizeMode="contain"
              />
            </TouchableOpacity>
          </View>
          <View style={styles.itemCenter}>
            {title ? (
              <Text numberOfLines={1} ellipsizeMode="tail" style={[styles.title, { ...titleStyle }]}>
                {title}
              </Text>
            ) : (
              <DS.Frame style={styles.logoContainer}>
                {isSubscribed && showPremiumIcon && <CrownIcon fill={DS.palette['Global/Black']} />}
                {pokerLogo}
              </DS.Frame>
            )}
          </View>
        </>
      ) : (
        <View style={[styles.itemLeftContainer, title ? styles.itemLeftContainerFill : null]}>
          <TouchableOpacity
            style={{
              ...styles.itemLeft,
              ...(title ? styles.itemLeftFit : null),
              display: isLeftItemDisplay,
              opacity: hideBackButton ? 0 : 1
            }}
            onPress={leftPress}
            disabled={hideBackButton}
          >
            <Image
              style={[styles.leftIcon, { ...leftIconStyle }]}
              source={leftIcon ? leftIcon : backIcon}
              resizeMode="contain"
            />
          </TouchableOpacity>

          {title ? (
            <Text
              numberOfLines={1}
              ellipsizeMode="tail"
              adjustsFontSizeToFit
              minimumFontScale={0.7}
              style={[styles.title, styles.titleFill, { ...titleStyle }]}
            >
              {title}
            </Text>
          ) : (
            <DS.Frame style={styles.logoContainer}>
              {isSubscribed && showPremiumIcon && <CrownIcon fill={DS.palette['Global/Black']} />}
              {pokerLogo}
            </DS.Frame>
          )}
        </View>
      )}


      <View style={styles.itemRightContainer}>
        {showEyeIcon ? (
          <ViewObserverButton disabled={!(observerPlayers && observerPlayers.length > 0)} />
        ) : !(isAdmin || isSuperAdmin) ? (
          <TouchableOpacity
            onPress={onOpenCheatSheet}
            style={[styles.eyeEmptyView, { justifyContent: 'center', marginRight: 8 }]}
          >
            <DS.Center style={{ flexDirection: 'row', gap: 6 }}>
              <DS.Text variant="Caption/Regular" color={showDarkKeyIcon ? 'Global/Black' : 'Global/White'}>
                Key
              </DS.Text>
              <DS.Icon name="Key" size={24} color={showDarkKeyIcon ? 'Global/Black' : 'Global/White'} />
            </DS.Center>
          </TouchableOpacity>
        ) : null}
        {showDiamonds && (
          <DiamondCountDisplay
            textColor={DS.palette[showDarkDiamondsText ? 'Global/Black' : 'Global/White']}
          />
        )}
        {rightIcon ? (
          <TouchableOpacity style={styles.itemRight} onPress={rightPress}>
            <Image style={[styles.rightIcon, { ...rightIconStyle }]} source={rightIcon} resizeMode="contain" />
          </TouchableOpacity>
        ) : null}
      </View>
    </View>
  )
})
